import { useEffect } from 'react';
import { Canvas } from 'fabric';
import { nanoid } from 'nanoid';

import { DrawData } from './draw.constants';
import { addArtboard } from './canvas/logic/draw-objects';
import { useDrawFontLoader } from './canvas/use-draw-font-loader';
import { getMergedDrawObjects } from './canvas/logic/get-merged-draw-objects';
import {
  initialiseAndPositionCanvas,
  reviveSavedObjects,
} from './canvas/logic/draw-revive';
import { grey1 } from 'approot/shared/styles/colours.style';
import * as Styled from './draw-print-answers-canvas.style';
import { LanguageCodes } from 'global/constants';

type Props = {
  id: string;
  imageUrl?: string | null;
  dimensionsData: {
    width: number;
    height: number;
  };
  containerEl: HTMLElement;
  answer?: DrawData;
  questionData?: DrawData | null;
  language: LanguageCodes;
  onRenderComplete: () => void;
};

const DrawPrintAnswersCanvas = ({
  id,
  imageUrl,
  dimensionsData,
  containerEl,
  answer,
  questionData,
  language,
  onRenderComplete,
}: Props) => {
  const CANVAS_CONTAINER_ID = `answers-inline-${id}-canvas-container`;
  const FABRIC_ELEMENT_ID = `answers-inline-fabric-${id}-${nanoid()}-canvas-container`;

  useEffect(() => {
    const canvas = new Canvas(FABRIC_ELEMENT_ID, {
      selection: false,
      width: containerEl.offsetWidth,
      height: containerEl.offsetHeight,
      preserveObjectStacking: true,
    });

    const artboard = addArtboard(canvas, dimensionsData);
    artboard.stroke = grey1;

    async function renderCanvasToImage() {
      return new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = canvas.toDataURL({
          format: 'png',
          multiplier: 2,
        });
        img.onload = () => {
          resolve(true);
        };
        containerEl
          .querySelector(`#${CANVAS_CONTAINER_ID}-image`)
          ?.appendChild(img);
      });
    }

    async function initialiseCanvas() {
      const combinedData = getMergedDrawObjects(answer, questionData);

      if (combinedData?.objects.length) {
        await reviveSavedObjects({
          canvas,
          artboard,
          drawingData: combinedData,
          isCreateMode: false,
          language,
        });
        await initialiseAndPositionCanvas(
          canvas,
          dimensionsData,
          imageUrl,
          artboard
        );
        await renderCanvasToImage();
        onRenderComplete();
      } else {
        await initialiseAndPositionCanvas(
          canvas,
          dimensionsData,
          imageUrl,
          artboard
        );
        await renderCanvasToImage();
        onRenderComplete();
      }
    }

    initialiseCanvas();

    return () => {
      if (canvas) {
        canvas.dispose();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Styled.DrawPrintAnswersCanvas id={CANVAS_CONTAINER_ID}>
      <canvas id={FABRIC_ELEMENT_ID}></canvas>
      <Styled.DrawPrintAnswersCanvasInner id={`${CANVAS_CONTAINER_ID}-image`} />
    </Styled.DrawPrintAnswersCanvas>
  );
};

const DrawPrintAnswersCanvasWrapper = (props: Props) => {
  const isFontsLoaded = useDrawFontLoader();
  return isFontsLoaded ? <DrawPrintAnswersCanvas {...props} /> : null;
};

export default DrawPrintAnswersCanvasWrapper;
